/**
 * <custom-designer>
 * Theme-native custom design uploader + live mockup preview.
 *
 * Lets a customer upload artwork, composite it onto the product photo within a
 * defined print area (pan / zoom / rotate), then add to cart. The original
 * artwork and the rendered mockup are attached to the order as line-item
 * properties by associating this block's inputs with the product form via the
 * HTML `form` attribute (see assets/product-form.js — it serialises the form
 * with `new FormData(form)`, which includes form-associated inputs and files).
 *
 * No backend, no third-party libraries.
 */
class CustomDesigner extends HTMLElement {
  #img = null; // the uploaded artwork (ImageBitmap | HTMLImageElement)
  #base = null; // the product/base image drawn behind the artwork
  #ctx = null;
  #state = { x: 0, y: 0, scale: 1, rotation: 0 }; // artwork transform within the print area
  #dragging = false;
  #pointer = { x: 0, y: 0 };
  #area = { x: 0.25, y: 0.25, w: 0.5, h: 0.5 }; // print area as fractions of the canvas
  #maxBytes = 10 * 1024 * 1024;
  #accept = ['image/png', 'image/jpeg', 'image/webp'];
  #require = false;
  #renderRaf = 0;
  #mockupTimer = 0;

  connectedCallback() {
    this.canvas = this.querySelector('[data-cd-canvas]');
    this.fileInput = this.querySelector('[data-cd-file]');
    this.mockupInput = this.querySelector('[data-cd-mockup]');
    this.placementInput = this.querySelector('[data-cd-placement]');
    this.dropzone = this.querySelector('[data-cd-dropzone]');
    this.controls = this.querySelector('[data-cd-controls]');
    this.scaleInput = this.querySelector('[data-cd-scale]');
    this.rotateInput = this.querySelector('[data-cd-rotate]');
    this.resetBtn = this.querySelector('[data-cd-reset]');
    this.errorEl = this.querySelector('[data-cd-error]');

    if (!this.canvas || !this.fileInput) return;
    this.#ctx = this.canvas.getContext('2d');

    // Read configuration from data attributes (set in the Liquid block).
    const ds = this.dataset;
    this.#area = {
      x: clamp01(parseFloat(ds.areaX) / 100),
      y: clamp01(parseFloat(ds.areaY) / 100),
      w: clamp01(parseFloat(ds.areaW) / 100),
      h: clamp01(parseFloat(ds.areaH) / 100),
    };
    if (ds.maxSize) this.#maxBytes = parseFloat(ds.maxSize) * 1024 * 1024;
    if (ds.accept) this.#accept = ds.accept.split(',').map((s) => s.trim()).filter(Boolean);
    this.#require = ds.require === 'true';

    this.#associateWithProductForm();
    this.#bindEvents();
    this.#loadBaseImage(ds.baseImage);
    this.#applyGate(); // disable add-to-cart up front if a design is required
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.#renderRaf);
  }

  /* ---------- product form wiring ---------- */

  #productForm() {
    const scope = this.closest('[data-section-id], .shopify-section') || document;
    return (
      scope.querySelector('product-form-component form[data-type="add-to-cart-form"]') ||
      document.querySelector('product-form-component form[data-type="add-to-cart-form"]')
    );
  }

  #associateWithProductForm() {
    const form = this.#productForm();
    if (!form) return;
    if (!form.id) form.id = `CustomDesignerForm-${Math.floor(performance.now())}`;
    // Associate every property input with the product form so FormData(form) picks them up.
    this.querySelectorAll('[data-cd-prop]').forEach((el) => el.setAttribute('form', form.id));
  }

  #addToCartButton() {
    const form = this.#productForm();
    return form ? form.querySelector('button[name="add"], [ref="addToCartButton"]') : null;
  }

  #applyGate() {
    if (!this.#require) return;
    const btn = this.#addToCartButton();
    if (!btn) return;
    if (this.#img) {
      btn.removeAttribute('disabled');
      btn.removeAttribute('aria-disabled');
    } else {
      btn.setAttribute('disabled', '');
      btn.setAttribute('aria-disabled', 'true');
    }
  }

  /* ---------- events ---------- */

  #bindEvents() {
    this.fileInput.addEventListener('change', (e) => this.#onFiles(e.target.files));

    if (this.dropzone) {
      ['dragenter', 'dragover'].forEach((t) =>
        this.dropzone.addEventListener(t, (e) => {
          e.preventDefault();
          this.dropzone.classList.add('is-dragover');
        })
      );
      ['dragleave', 'drop'].forEach((t) =>
        this.dropzone.addEventListener(t, (e) => {
          e.preventDefault();
          this.dropzone.classList.remove('is-dragover');
        })
      );
      this.dropzone.addEventListener('drop', (e) => this.#onFiles(e.dataTransfer.files));
    }

    // Pan the artwork by dragging on the canvas.
    this.canvas.addEventListener('pointerdown', (e) => {
      if (!this.#img) return;
      this.#dragging = true;
      this.#pointer = { x: e.offsetX, y: e.offsetY };
      this.canvas.setPointerCapture(e.pointerId);
    });
    this.canvas.addEventListener('pointermove', (e) => {
      if (!this.#dragging) return;
      const ratio = this.canvas.width / this.canvas.clientWidth;
      this.#state.x += (e.offsetX - this.#pointer.x) * ratio;
      this.#state.y += (e.offsetY - this.#pointer.y) * ratio;
      this.#pointer = { x: e.offsetX, y: e.offsetY };
      this.#scheduleRender();
    });
    const endDrag = (e) => {
      if (!this.#dragging) return;
      this.#dragging = false;
      try { this.canvas.releasePointerCapture(e.pointerId); } catch (_) {}
      this.#updateMockup();
    };
    this.canvas.addEventListener('pointerup', endDrag);
    this.canvas.addEventListener('pointercancel', endDrag);

    if (this.scaleInput)
      this.scaleInput.addEventListener('input', () => {
        this.#state.scale = parseFloat(this.scaleInput.value);
        this.#scheduleRender();
        this.#debouncedMockup();
      });
    if (this.rotateInput)
      this.rotateInput.addEventListener('input', () => {
        this.#state.rotation = parseFloat(this.rotateInput.value);
        this.#scheduleRender();
        this.#debouncedMockup();
      });
    if (this.resetBtn) this.resetBtn.addEventListener('click', () => this.#resetTransform());
  }

  /* ---------- file handling ---------- */

  async #onFiles(files) {
    const file = files && files[0];
    if (!file) return;
    this.#clearError();

    if (this.#accept.length && !this.#accept.includes(file.type)) {
      return this.#showError(`Unsupported file type. Please upload: ${this.#accept.join(', ')}.`);
    }
    if (file.size > this.#maxBytes) {
      return this.#showError(`File is too large. Max ${(this.#maxBytes / 1024 / 1024).toFixed(0)} MB.`);
    }

    try {
      this.#img = await loadImage(file);
    } catch (_) {
      return this.#showError('Sorry, that image could not be read. Try a different file.');
    }

    // Put the chosen file onto the real (form-associated) file input so it uploads
    // as the "Design file" line-item property.
    if (files !== this.fileInput.files) {
      try {
        const dt = new DataTransfer();
        dt.items.add(file);
        this.fileInput.files = dt.files;
      } catch (_) {
        /* drag-drop fallback: original file still rendered; see note in #updateMockup */
      }
    }

    if (this.controls) this.controls.hidden = false;
    this.dropzone?.classList.add('has-file');
    this.#resetTransform();
    this.#applyGate();
  }

  /* ---------- rendering ---------- */

  #loadBaseImage(url) {
    if (!url) { this.#sizeToFallback(); return; }
    const img = new Image();
    img.crossOrigin = 'anonymous'; // Shopify CDN serves CORS headers so the canvas stays exportable
    img.onload = () => {
      this.#base = img;
      this.#sizeCanvas(img.naturalWidth, img.naturalHeight);
      this.#scheduleRender();
    };
    img.onerror = () => this.#sizeToFallback();
    img.src = url;
  }

  #sizeToFallback() {
    this.#sizeCanvas(1000, 1000);
    this.#scheduleRender();
  }

  #sizeCanvas(w, h) {
    const maxW = 1000;
    const scale = w > maxW ? maxW / w : 1;
    this.canvas.width = Math.round(w * scale);
    this.canvas.height = Math.round(h * scale);
  }

  #areaPx() {
    return {
      x: this.#area.x * this.canvas.width,
      y: this.#area.y * this.canvas.height,
      w: this.#area.w * this.canvas.width,
      h: this.#area.h * this.canvas.height,
    };
  }

  #resetTransform() {
    this.#state = { x: 0, y: 0, scale: 1, rotation: 0 };
    if (this.scaleInput) this.scaleInput.value = '1';
    if (this.rotateInput) this.rotateInput.value = '0';
    this.#scheduleRender();
    this.#updateMockup();
  }

  #scheduleRender() {
    cancelAnimationFrame(this.#renderRaf);
    this.#renderRaf = requestAnimationFrame(() => this.#render());
  }

  #render() {
    const ctx = this.#ctx;
    const { width: cw, height: ch } = this.canvas;
    ctx.clearRect(0, 0, cw, ch);

    if (this.#base) ctx.drawImage(this.#base, 0, 0, cw, ch);
    else { ctx.fillStyle = '#f4f4f4'; ctx.fillRect(0, 0, cw, ch); }

    const area = this.#areaPx();

    if (this.#img) {
      ctx.save();
      ctx.beginPath();
      ctx.rect(area.x, area.y, area.w, area.h);
      ctx.clip();

      // Base "contain" fit of the artwork inside the print area, then apply user transform.
      const iw = this.#img.width;
      const ih = this.#img.height;
      const fit = Math.min(area.w / iw, area.h / ih);
      const drawW = iw * fit * this.#state.scale;
      const drawH = ih * fit * this.#state.scale;
      const cx = area.x + area.w / 2 + this.#state.x;
      const cy = area.y + area.h / 2 + this.#state.y;

      ctx.translate(cx, cy);
      ctx.rotate((this.#state.rotation * Math.PI) / 180);
      ctx.drawImage(this.#img, -drawW / 2, -drawH / 2, drawW, drawH);
      ctx.restore();
    }

    // Dashed outline of the print area as a guide.
    ctx.save();
    ctx.strokeStyle = 'rgba(0,0,0,0.45)';
    ctx.setLineDash([6, 5]);
    ctx.lineWidth = Math.max(1, cw / 500);
    ctx.strokeRect(area.x, area.y, area.w, area.h);
    ctx.restore();
  }

  /* ---------- mockup + placement output ---------- */

  #debouncedMockup() {
    clearTimeout(this.#mockupTimer);
    this.#mockupTimer = setTimeout(() => this.#updateMockup(), 200);
  }

  #updateMockup() {
    // Save the placement params (small text property — never a data URL).
    if (this.placementInput) {
      this.placementInput.value = JSON.stringify({
        x: Math.round(this.#state.x),
        y: Math.round(this.#state.y),
        scale: Number(this.#state.scale.toFixed(3)),
        rotation: Math.round(this.#state.rotation),
        area: this.#area,
      });
    }

    if (!this.#img || !this.mockupInput) return;
    // Render a flattened guide-free mockup for the order.
    this.#renderFlat();
    try {
      this.canvas.toBlob(
        (blob) => {
          if (!blob) return;
          try {
            const dt = new DataTransfer();
            dt.items.add(new File([blob], 'mockup.jpg', { type: 'image/jpeg' }));
            this.mockupInput.files = dt.files;
          } catch (_) {
            /* DataTransfer unsupported: fall back to Design file + Placement only */
          }
          this.#scheduleRender(); // restore the on-screen guide
        },
        'image/jpeg',
        0.9
      );
    } catch (_) {
      // Tainted canvas (base image without CORS) — keep Design file + Placement.
      this.#scheduleRender();
    }
  }

  // Same as #render but without the dashed guide, used only for the exported mockup.
  #renderFlat() {
    const ctx = this.#ctx;
    const { width: cw, height: ch } = this.canvas;
    ctx.clearRect(0, 0, cw, ch);
    if (this.#base) ctx.drawImage(this.#base, 0, 0, cw, ch);
    else { ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, cw, ch); }
    if (!this.#img) return;
    const area = this.#areaPx();
    ctx.save();
    ctx.beginPath();
    ctx.rect(area.x, area.y, area.w, area.h);
    ctx.clip();
    const fit = Math.min(area.w / this.#img.width, area.h / this.#img.height);
    const drawW = this.#img.width * fit * this.#state.scale;
    const drawH = this.#img.height * fit * this.#state.scale;
    ctx.translate(area.x + area.w / 2 + this.#state.x, area.y + area.h / 2 + this.#state.y);
    ctx.rotate((this.#state.rotation * Math.PI) / 180);
    ctx.drawImage(this.#img, -drawW / 2, -drawH / 2, drawW, drawH);
    ctx.restore();
  }

  /* ---------- errors ---------- */

  #showError(msg) {
    if (this.errorEl) {
      this.errorEl.textContent = msg;
      this.errorEl.hidden = false;
    }
  }
  #clearError() {
    if (this.errorEl) {
      this.errorEl.textContent = '';
      this.errorEl.hidden = true;
    }
  }
}

function clamp01(n) {
  if (Number.isNaN(n)) return 0;
  return Math.min(1, Math.max(0, n));
}

async function loadImage(file) {
  if ('createImageBitmap' in window) {
    try {
      return await createImageBitmap(file);
    } catch (_) {
      /* fall through to <img> */
    }
  }
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      resolve(img);
      URL.revokeObjectURL(url);
    };
    img.onerror = reject;
    img.src = url;
  });
}

if (!customElements.get('custom-designer')) {
  customElements.define('custom-designer', CustomDesigner);
}
